import React from 'react';
import { BillData } from '../types/billing';

interface BillDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  bill: BillData | null;
  onPrint: (bill: BillData) => void;
}

const BillDetailsModal: React.FC<BillDetailsModalProps> = ({
  isOpen,
  onClose,
  bill,
  onPrint
}) => {
  if (!isOpen || !bill) return null;

  const handlePrint = () => {
    onPrint(bill);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="bg-gradient-to-r from-avis-blue to-avis-dark text-white px-8 py-6 rounded-t-2xl">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-2xl font-bold">Bill Details</h2>
              <p className="text-blue-100 mt-1">Invoice #{bill.invoiceNumber}</p>
            </div>
            <div className="flex space-x-3">
              <button
                onClick={handlePrint}
                className="bg-white text-avis-blue px-4 py-2 rounded-lg hover:bg-blue-50 transition-colors font-semibold flex items-center space-x-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                </svg>
                <span>Print Bill</span>
              </button>
              <button
                onClick={onClose}
                className="bg-white bg-opacity-20 text-white px-4 py-2 rounded-lg hover:bg-opacity-30 transition-colors"
              >
                ✕
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-8">
          {/* Bill Header */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Bill Information</h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-600">Invoice Number:</span>
                  <span className="font-semibold">{bill.invoiceNumber}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Invoice Date:</span>
                  <span className="font-semibold">{new Date(bill.invoiceDate).toLocaleDateString()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Work Order Number:</span>
                  <span className="font-semibold">{bill.workOrderNumber}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Work Order Date:</span>
                  <span className="font-semibold">
                    {bill.workOrderDate && bill.workOrderDate.toUpperCase() !== 'N/A' 
                      ? (() => {
                          try {
                            return new Date(bill.workOrderDate).toLocaleDateString();
                          } catch {
                            return bill.workOrderDate;
                          }
                        })()
                      : bill.workOrderDate || 'N/A'}
                  </span>
                </div>
                {bill.periodOfWork && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">Period Of Work:</span>
                    <span className="font-semibold">{bill.periodOfWork}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-gray-600">Status:</span>
                  <span className={`px-2 py-1 text-xs rounded-full font-semibold ${
                    bill.status === 'Paid' ? 'bg-green-100 text-green-800' :
                    bill.status === 'Sent' ? 'bg-blue-100 text-blue-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {bill.status}
                  </span>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Client Information</h3>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-600">Client Name:</span>
                  <span className="font-semibold">{bill.clientName}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">GST Number:</span>
                  <span className="font-semibold">{bill.clientGSTN}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">State:</span>
                  <span className="font-semibold">{bill.clientState} ({bill.clientStateCode})</span>
                </div>
                <div className="text-gray-600 mt-4">
                  <strong>Address:</strong><br />
                  {bill.clientAddress}
                </div>
              </div>
            </div>
          </div>

          {/* Company Information */}
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Company Information</h3>
            <div className="bg-gray-50 p-6 rounded-xl">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-semibold text-gray-900 mb-2">{bill.companyName}</h4>
                  <p className="text-gray-600 mb-2">{bill.companySubtitle}</p>
                  <p className="text-gray-600 text-sm">{bill.companyAddress}</p>
                </div>
                <div>
                  <div className="space-y-2 text-sm">
                    <div><strong>Phone:</strong> {bill.companyPhone}</div>
                    <div><strong>Email:</strong> {bill.companyEmail}</div>
                    <div><strong>GSTN:</strong> {bill.companyGSTN}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Items Table */}
          <div className="mb-8">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Bill Items</h3>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sr. No</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">SAC</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Qty</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Rate</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Total</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {bill.items.map((item, index) => (
                    <tr key={index}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{item.srNo}</td>
                      <td className="px-6 py-4 text-sm text-gray-900">{item.description}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{item.sac}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{item.qty}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {(() => {
                          const rounded = Math.round(item.rate);
                          return `₹${rounded.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
                        })()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-right">
                        {(() => {
                          const rounded = Math.round(item.totalAmount);
                          return `₹${rounded.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
                        })()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Bill Summary */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Bill Summary</h3>
              <div className="bg-gray-50 p-6 rounded-xl space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-600">Subtotal:</span>
                  <span className="font-semibold">
                    {(() => {
                      const rounded = Math.round(bill.subTotal);
                      return `₹${rounded.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
                    })()}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">CGST ({bill.gstRate/2}%):</span>
                  <span className="font-semibold">
                    {(() => {
                      const rounded = Math.round(bill.cgstAmount);
                      return `₹${rounded.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
                    })()}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">SGST ({bill.gstRate/2}%):</span>
                  <span className="font-semibold">
                    {(() => {
                      const rounded = Math.round(bill.sgstAmount);
                      return `₹${rounded.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
                    })()}
                  </span>
                </div>
                <div className="flex justify-between border-t pt-3">
                  <span className="text-lg font-semibold text-gray-900">Total Amount:</span>
                  <span className="text-lg font-bold text-avis-blue">
                    {(() => {
                      const rounded = Math.round(bill.totalAmount);
                      return `₹${rounded.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
                    })()}
                  </span>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Banking Details</h3>
              <div className="bg-gray-50 p-6 rounded-xl space-y-3">
                <div className="flex justify-between">
                  <span className="text-gray-600">Bank Name:</span>
                  <span className="font-semibold">{bill.bankDetails.bankName}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Branch:</span>
                  <span className="font-semibold">{bill.bankDetails.branch}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Account Number:</span>
                  <span className="font-semibold">{bill.bankDetails.accountNumber}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">IFSC Code:</span>
                  <span className="font-semibold">{bill.bankDetails.ifscCode}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Terms and Conditions */}
          {bill.termsAndConditions && bill.termsAndConditions.length > 0 && (
            <div className="mt-8">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Terms and Conditions</h3>
              <div className="bg-gray-50 p-6 rounded-xl">
                <ul className="list-disc list-inside space-y-2 text-gray-700">
                  {bill.termsAndConditions.map((term, index) => (
                    <li key={index}>{term}</li>
                  ))}
                </ul>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-8 py-6 rounded-b-2xl flex justify-end space-x-4">
          <button
            onClick={handlePrint}
            className="bg-avis-blue text-white px-6 py-3 rounded-xl hover:bg-avis-dark transition-colors font-semibold flex items-center space-x-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
            </svg>
            <span>Print Bill</span>
          </button>
          <button
            onClick={onClose}
            className="border border-gray-300 text-gray-700 px-6 py-3 rounded-xl hover:bg-gray-100 transition-colors font-semibold"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default BillDetailsModal;
